<template>
  <div>
    <el-card
      v-loading="loading"
      shadow="hover"
      :body-style="{padding: '20px 30px'}"
      class="mainCard clear-card"
    >
      <div class="title">
        <div class="left defaultCursor">
          {{ $t('common.account') }}
        </div>
        <div class="right">
          <el-button
            :type="edit ? 'plain' : 'primary'"
            round
            @click="clickEdit"
          >
            <span v-if="!edit">
              {{ $t('common.edit') }} <i class="el-icon-edit"></i>
            </span>
            <span v-else>
              {{ $t('common.save') }}
            </span>
          </el-button>
        </div>
      </div>

      <div class="content">
        <el-form
          ref="settingsForm"
          :model="settingsForm"
        >
          <el-row :gutter="20">
            <el-col :span="tripleCol">
              <el-form-item :label="$t('common.firstName')">
                <el-input
                  v-model="settingsForm.user.first_name"
                  :disabled="!edit"
                  :suffix-icon="edit ? 'el-icon-edit' : ''"
                  @change="syncUpdateData('user.first_name', ...arguments)"
                />
              </el-form-item>
            </el-col>
            <el-col :span="tripleCol">
              <el-form-item :label="$t('common.lastName')">
                <el-input
                  v-model="settingsForm.user.last_name"
                  :disabled="!edit"
                  :suffix-icon="edit ? 'el-icon-edit' : ''"
                  @change="syncUpdateData('user.last_name', ...arguments)"
                />
              </el-form-item>
            </el-col>
            <el-col :span="tripleCol">
              <el-form-item :label="$t('common.email')">
                <el-input
                  v-model="settingsForm.user.email"
                  class="emailInput"
                  disabled
                />
              </el-form-item>
            </el-col>

            <el-col :span="tripleCol">
              <el-form-item>
                <div style="font-size: 14px; line-height: 20px;">
                  {{ $t('common.timezone') }}
                </div>
                <div
                  v-if="!edit"
                  style="display: inline-block;"
                >
                  {{ displayTimezone }}
                </div>

                <div v-else>
                  <el-select
                    v-if="timezones.length > 0"
                    v-model="settingsForm.user.time_zone"
                    filterable
                    :placeholder="$t('settings.searchTimezone')"
                    @change="syncUpdateData('user.time_zone', ...arguments)"
                  >
                    <el-option
                      v-for="item in timezones"
                      :key="item.value"
                      :label="item.label"
                      :value="item.value"
                    />
                  </el-select>
                </div>
              </el-form-item>
            </el-col>

            <el-col :span="tripleCol">
              <div class="password">
                <div class="title">
                  {{ $t('settings.changePassword') }}
                </div>

                <el-button
                  round
                  class="changePasswordBtn"
                  @click="changePassword = true"
                >
                  {{ $t('common.change') }}
                </el-button>
              </div>
            </el-col>
          </el-row>
        </el-form>
      </div>
    </el-card>

    <ChangePasswordModal
      :show.sync="changePassword"
    />
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex';
import api from '@/api';
import ChangePasswordModal from './ChangePasswordModal.vue';

export default {
  name: 'AccountSettings',

  components: {
    ChangePasswordModal,
  },

  data() {
    return {
      settingsForm: {
        user: {
          first_name: '',
          last_name: '',
          email: '',
          time_zone: '',
          locations: '',
        },
        profile: {
          corporate_key: '',
          languages: '',
          ap_reminder: 'daily',
          ap_reminder_settings: {
            day_list: [],
          },
          po_number: '',
          business_line: '',
          payable_address: '',
          cost_centre: '',
        },
      },
      updateData: {},
      changePassword: false,
      edit: false,
      rules: {},
      timezones: [],
      countries: [],
      languages: [],
      selectedCountry: null,
      loadingList: [],
    };
  },

  computed: {
    ...mapState({
      user: (state) => state.user,
    }),

    displayTimezone() {
      const value = this.settingsForm.user.time_zone;
      if (!value) return '';

      const period = this.timezones.find((per) => per.value === value);

      if (period) {
        return period.display;
      }
      return '';
    },

    selectedLocation: {
      get() {
        if (!this.settingsForm.user.locations || this.settingsForm.user.locations.length < 1) {
          return { name: '' };
        }
        return this.settingsForm.user.locations[0];
      },
      set(location) {
        this.settingsForm.user.locations = [location];
      },
    },

    selectedLanguage: {
      get() {
        if (!this.settingsForm.profile.languages
          || this.settingsForm.profile.languages.length < 1) {
          return '';
        }
        return this.settingsForm.profile.languages[0].value;
      },
      set(language) {
        this.languages.forEach((el) => {
          if (el.value === language) {
            this.settingsForm.profile.languages = [el];
          }
        });
      },
    },

    tripleCol() {
      let col = 8;
      if (this.$mq === 'mobile') {
        col = 24;
      } else if (this.$mq === 'tablet') {
        col = 12;
      }
      return col;
    },

    loading() {
      let result = false;
      if (this.loadingList) {
        result = !!this.loadingList.length;
      }
      return result;
    },
  },

  async created() {
    this.getTimezones();
    this.getCountries();
    this.getLanguages();
    this.initForm(this.user);
  },

  methods: {
    ...mapActions('processStatus', [
      'fetchProcess',
    ]),

    ...mapMutations('user', [
      'loginUser',
    ]),

    syncUpdateData(field, v) {
      let value = v;
      if (field === 'profile.ap_reminder' && value === 'none') {
        this.$alert(
          `${this.$t('settings.alert.neverRemind')}`,
          '',
          {
            type: 'warning',
            confirmButtonText: 'OK',
          },
        );
      }

      if (field === 'user.locations') {
        value = [v];
      }

      if (field === 'profile.languages') {
        this.languages.forEach((el) => {
          if (el.value === v) {
            value = [el];
          }
        });
      }

      let last;
      this.updateData = field.split('.').reduce((obj, val, index, arr) => {
        const o = obj;
        if (typeof last === 'object') {
          if (index === arr.length - 1) {
            last[val] = value;
          } else if (!last[val]) {
            last[val] = {};
          }
          last = last[val];
        } else {
          if (!o[val]) {
            o[val] = {};
          }
          last = o[val];
        }

        return o;
      }, this.updateData);

      if (field === 'profile.ap_reminder') {
        if (value === 'daily') {
          this.updateData.profile.ap_reminder_settings = {};
          const dayList = this.settingsForm.profile.ap_reminder_settings.day_list;
          this.updateData.profile.ap_reminder_settings.day_list = dayList;
        } else if (value === 'none') {
          delete this.updateData.profile.ap_reminder_settings;
        }
      }

      // exception rules for ap_reminder field because back-end rules are not consistent
      if (!this.updateData.profile) this.updateData.profile = {};
      this.updateData.profile.ap_reminder = this.settingsForm.profile.ap_reminder;
      if (this.updateData.profile.ap_reminder === 'daily') {
        // eslint-disable-next-line max-len
        this.updateData.profile.ap_reminder_settings = this.settingsForm.profile.ap_reminder_settings;
      }
    },

    async getTimezones() {
      try {
        this.loadingList.push('run');
        const response = await api.getTimezoneList();
        this.timezones = response.data;
      } catch (err) {
        console.log(err);
        if (err.msg) {
          this.$message.error(err.msg);
        }
      } finally {
        this.loadingList.pop();
      }
    },

    async getCountries() {
      try {
        this.loadingList.push('run');
        const response = await api.getCountriesList();
        this.countries = response.data;
      } catch (err) {
        console.log(err);
        if (err.msg) {
          this.$message.error(err.msg);
        }
      } finally {
        this.loadingList.pop();
      }
    },

    async getLanguages() {
      try {
        this.loadingList.push('run');
        const response = await api.getCoachLanguages();
        this.languages = response.data;
      } catch (err) {
        console.log(err);
        if (err.msg) {
          this.$message.error(err.msg);
        }
      } finally {
        this.loadingList.pop();
      }
    },

    initForm(data) {
      this.updateData = {};
      const { profile } = data;

      this.settingsForm.user.first_name = data.user.first_name;
      this.settingsForm.user.last_name = data.user.last_name;
      this.settingsForm.user.email = data.user.email;
      this.settingsForm.user.time_zone = data.user.time_zone;
      this.settingsForm.user.locations = data.user.locations;

      if (profile) {
        const hasDayList = profile.ap_reminder_settings && profile.ap_reminder_settings.day_list;

        this.settingsForm.profile.corporate_key = profile.corporate_key;
        this.settingsForm.profile.ap_reminder = profile.ap_reminder;
        this.settingsForm.profile.ap_reminder_settings.day_list = hasDayList
          ? profile.ap_reminder_settings.day_list
          : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
        this.settingsForm.profile.languages = data.profile.languages;
        this.settingsForm.profile.po_number = data.profile.po_number;
        this.settingsForm.profile.business_line = data.profile.business_line;
        this.settingsForm.profile.cost_centre = data.profile.cost_centre;
        this.settingsForm.profile.payable_country = data.profile.payable_country;
        this.settingsForm.profile.payable_address = data.profile.payable_address;
      }
    },

    async clickEdit() {
      if (this.edit) {
        await this.saveSettings();
      } else {
        this.edit = !this.edit;
      }
    },

    async saveSettings() {
      try {
        this.loadingList.push('run');
        if (Object.entries(this.updateData).length > 0) {
          const response = await api.updateINGUser(this.updateData);
          this.initForm(response.data);
          this.$message.success('Your settings have been saved');
          await this.loginUser(response.data);
        }
        if (this.user.user.role_type === 'leader') {
          this.fetchProcess();
        }
        this.edit = false;
      } catch (err) {
        console.log(err);
        if (err.msg) {
          this.$message.error(err.msg);
        }
      } finally {
        this.loadingList.pop();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.el-card {
  margin-bottom: 20px;

  .emptyPlaceholder {
    position: relative;
    width: 100%;
    display: inline-block;
    font-size: 16px;
  }

  .el-row.others {
    .el-col > div:last-child {
      margin-bottom: 10px;
    }
  }

  .title {
    margin-bottom: 12px;
    font-weight: bold;
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: default;

    .left {
      font-size: 16px;
    }

    &.actionPlan {
      justify-content: normal;

      .apTime {
        padding: 0 4px;
        font-size: 14px;
        font-weight: normal;
      }
    }
  }

  .el-row {
    margin-bottom: 20px;

    .APReminder {
      margin-bottom: 50px;
    }

    &:last-child {
      margin: 0;
    }

    .timeZoneItem {
      margin: 0 0 10px;
    }
  }

  .password {
    padding-bottom: 5px;

    .changePasswordBtn {
      background-color: transparent;
      color: white;
    }
  }

  .warning {
    color: #f56c6c;
    font-size: 14px;
  }
}
</style>
